// TaskDetailItems: Mission items section for TaskDetailModal
// Renders items in an accordion with direct inline text input editing
// Separates read-only items from editable text fields (action zone)
// Save button appears contextually only when there are unsaved changes
// UPDATED: Added ClipboardList icon to accordion trigger for visual consistency with TaskActivityHistory

import { useState, useEffect } from 'react';
import { Check, CheckCircle2, ChevronDown, Save, AlertCircle, ClipboardList } from 'lucide-react';
import type { AssignedTaskItem } from '../../lib/execution';

interface TaskDetailItemsProps {
  taskId: string;
  items: AssignedTaskItem[];
  onTextInputBatchSave: (values: Record<string, string>) => Promise<void>;
}

export function TaskDetailItems({ taskId, items, onTextInputBatchSave }: TaskDetailItemsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [editedValues, setEditedValues] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedSuccess, setSavedSuccess] = useState(false);

  // Reset local state when a different task is opened
  useEffect(() => {
    setEditedValues({});
    setSaveError(null);
    setSavedSuccess(false);
    setIsOpen(false);
  }, [taskId]);

  const sortedItems = [...items].sort((a, b) => a.order_index - b.order_index);
  const textInputItems = sortedItems.filter(item => item.requires_text_input);
  const readOnlyItems = sortedItems.filter(item => !item.requires_text_input);

  const getDisplayValue = (item: AssignedTaskItem): string => {
    if (editedValues[item.id] !== undefined) return editedValues[item.id];
    return item.text_value || '';
  };

  const changedValues = Object.fromEntries(
    Object.entries(editedValues).filter(([id, value]) => {
      const original = items.find(i => i.id === id)?.text_value || '';
      return value !== original;
    })
  );
  const hasChanges = Object.keys(changedValues).length > 0;

  const filledCount = textInputItems.filter(i => !!getDisplayValue(i)).length;

  const handleSave = async () => {
    setSaveError(null);
    setIsSaving(true);
    try {
      await onTextInputBatchSave(changedValues);
      setSavedSuccess(true);
      setTimeout(() => setSavedSuccess(false), 2500);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="border-t border-gray-100 pt-5">
      {/* Accordion trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center text-sm font-medium text-gray-900 hover:text-gray-700 transition-colors"
      >
        <ClipboardList size={15} className="mr-1.5 text-gray-400 flex-shrink-0" />
        <span>Détails de la mission</span>
        {textInputItems.length > 0 && (
          <span className={`ml-2 text-xs font-normal ${filledCount === textInputItems.length ? 'text-emerald-600' : 'text-gray-500'}`}>
            ({filledCount}/{textInputItems.length} rempli{textInputItems.length > 1 ? 's' : ''})
          </span>
        )}
        <ChevronDown
          size={18}
          className={`ml-2 text-gray-500 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Accordion content */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-[700px] opacity-100 mt-3' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="space-y-4">
          {/* Read-only task items */}
          {readOnlyItems.length > 0 && (
            <div className="space-y-2">
              {readOnlyItems.map((item) => (
                <div key={item.id} className="flex items-start gap-2">
                  <Check size={15} className="text-gray-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-gray-700 leading-snug">{item.title}</p>
                </div>
              ))}
            </div>
          )}

          {/* Action zone: editable text input fields */}
          {textInputItems.length > 0 && (
            <div className="bg-gray-50 rounded-xl p-3.5 space-y-3.5 border border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Champs de saisie</p>

              {textInputItems.map((item) => {
                const displayValue = getDisplayValue(item);
                const isFilled = !!displayValue;
                const isModified = editedValues[item.id] !== undefined &&
                  editedValues[item.id] !== (item.text_value || '');

                return (
                  <div key={item.id}>
                    <div className="flex items-center gap-1.5 mb-1.5">
                      {isFilled ? (
                        <CheckCircle2 size={13} className="text-emerald-500 flex-shrink-0" />
                      ) : (
                        <div className={`w-3 h-3 rounded-full border-2 flex-shrink-0 ${
                          item.is_text_input_required ? 'border-amber-400' : 'border-gray-300'
                        }`} />
                      )}
                      <label className="text-xs font-medium text-gray-700 leading-none">
                        {item.title}
                        {item.is_text_input_required && (
                          <span className="ml-1 text-red-500">*</span>
                        )}
                      </label>
                    </div>
                    <input
                      type="text"
                      value={displayValue}
                      onChange={(e) =>
                        setEditedValues(prev => ({ ...prev, [item.id]: e.target.value }))
                      }
                      placeholder={item.text_input_placeholder || 'Entrez une valeur…'}
                      disabled={isSaving}
                      className={`w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white disabled:bg-gray-100 transition-colors ${
                        isModified
                          ? 'border-primary-300 ring-1 ring-primary-100'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    />
                  </div>
                );
              })}

              {/* Save / feedback area */}
              <div className="flex items-center justify-between pt-0.5 min-h-[28px]">
                <div className="flex-1">
                  {saveError && (
                    <p className="text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle size={12} />
                      {saveError}
                    </p>
                  )}
                  {savedSuccess && !hasChanges && (
                    <p className="text-xs text-emerald-600 flex items-center gap-1">
                      <CheckCircle2 size={12} />
                      Enregistré
                    </p>
                  )}
                </div>
                {hasChanges && (
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white text-xs font-medium rounded-lg hover:bg-primary-700 active:bg-primary-800 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed shadow-sm"
                  >
                    {isSaving ? (
                      <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Save size={13} />
                    )}
                    {isSaving ? 'Enregistrement…' : 'Enregistrer'}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
